import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { FaEnvelope, FaLock, FaSignInAlt, FaUserPlus, FaBan } from 'react-icons/fa';
import SEO from '../components/SEO';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Live countdown timer for lockout
  const updateCountdown = useCallback(() => {
    if (!lockedUntil) {
      setCountdown('');
      return;
    }
    const now = new Date();
    const lockDate = new Date(lockedUntil);
    const diff = lockDate - now;
    if (diff <= 0) {
      setLockedUntil(null);
      setCountdown('');
      setError('');
      return;
    }
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    setCountdown(
      `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    );
  }, [lockedUntil]);

  useEffect(() => {
    if (!lockedUntil) return;
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil, updateCountdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockedUntil && new Date(lockedUntil) > new Date()) return;

    setIsLoading(true);
    setError('');
    setAttemptsRemaining(null);

    const result = await login(email, password);

    if (result.success) {
      setLockedUntil(null);
      if (result.user?.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.message);
      // Check for lockout data in the error response
      if (result.locked) {
        setLockedUntil(result.lockedUntil);
        setAttemptsRemaining(null);
      } else if (result.attemptsRemaining !== undefined) {
        setAttemptsRemaining(result.attemptsRemaining);
      }
    }
    setIsLoading(false);
  };

  const isLocked = lockedUntil && new Date(lockedUntil) > new Date();

  return (
    <div className="min-h-screen bg-maroon-dark flex items-center justify-center p-4 pt-24 relative overflow-hidden">
      <SEO 
        title="Login • Taalsetu"
        description="Login to your Taalsetu account to access your Tabla courses, lessons, and personalized learning dashboard."
        url="https://www.taalsetu.com/login/"
        noindex
      />
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-gold/5 blur-[120px] rounded-full"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-gold/5 blur-[120px] rounded-full"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Login Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-10 border border-gold/10 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg overflow-hidden bg-white">
              <img src="/images/logo.jpg" alt="Taalsetu Logo" className="w-full h-full object-cover" />
            </div>
            <h2 className="text-4xl font-serif font-bold text-gold-light mb-2 tracking-tight">Welcome Back</h2>
            <p className="text-gold-light/50 font-sans font-medium">Continue your tabla learning journey</p>
          </div>

          {/* Lockout Banner */}
          {isLocked && (
            <div className="mb-6 bg-red-500/15 border border-red-500/40 rounded-2xl p-5 text-center">
              <FaBan className="text-red-400 text-3xl mx-auto mb-3" />
              <p className="text-red-300 font-semibold text-sm mb-1">Account Temporarily Locked</p>
              <p className="text-red-400/80 text-xs mb-3">Too many failed login attempts</p>
              <div className="bg-black/20 rounded-xl py-3 px-4 inline-block">
                <span className="text-red-300 text-xs font-medium">Try again in </span>
                <span className="text-white font-mono text-lg font-bold tracking-wider">{countdown}</span>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gold-light/40 group-focus-within:text-gold transition-colors">
                <FaEnvelope className="h-5 w-5" />
              </div>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30 font-sans"
                required
                disabled={isLocked}
              />
            </div>

            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gold-light/40 group-focus-within:text-gold transition-colors">
                <FaLock className="h-5 w-5" />
              </div>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-gold/10 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-all text-white placeholder-gold-light/30 font-sans"
                required
                disabled={isLocked}
              />
            </div>

            {error && !isLocked && (
              <div className={`px-4 py-3 rounded-2xl text-sm font-medium font-sans ${
                attemptsRemaining !== null && attemptsRemaining <= 1
                  ? 'bg-orange-500/15 border border-orange-500/50 text-orange-300'
                  : 'bg-red-500/10 border border-red-500/50 text-red-400'
              }`}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || isLocked}
              className={`w-full h-14 btn-gold !rounded-2xl flex items-center justify-center space-x-3 text-base ${isLocked ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <FaSignInAlt className="text-xl" />
                  <span>{isLocked ? 'Account Locked' : 'Sign In'}</span>
                </>
              )}
            </button>
            
            <div className="flex justify-end mt-2">
              <Link to="/forgot-password" className="text-sm text-gold-light/60 hover:text-gold transition-colors font-sans">
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gold/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-maroon-dark text-gold-light/40 rounded-full py-1 font-sans">New to Taalsetu?</span>
            </div>
          </div>

          <Link
            to="/register"
            className="w-full flex items-center justify-center space-x-2 py-4 bg-white/5 border border-gold/10 rounded-2xl text-gold-light font-bold hover:bg-gold/5 transition-all font-sans"
          >
            <FaUserPlus />
            <span>Create Account</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;